import type { FlowPlayer, QueueRankConfig } from "@rivaled/shared";
import { getTeamNames } from "@rivaled/shared";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Clock, Crown, Map as MapIcon, Minus, Skull, Trophy } from "lucide-react";

import { useSession } from "../../lib/auth-client";
import { trpc } from "../../lib/trpc";
import { cn } from "../../lib/utils";
import { PlayerCard } from "../../match-flow/components/PlayerCard";
import { getTeamColor } from "../../match-flow/lib/team-colors";
import { Button } from "../ui";
import { DisputeButton } from "./DisputeButton";
import { DisputeStatusBanner } from "./DisputeStatusBanner";
import type { MatchData } from "./matchHelpers";

type MatchCompletedViewProps = {
  match: MatchData;
  clubSlug?: string;
  disputeWindowHours?: number | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return "--:--";
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const secs = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatEloChange(change: number | null): { text: string; color: string } {
  if (change == null || change === 0) return { text: "+0", color: "text-surface-500" };
  if (change > 0) return { text: `+${change}`, color: "text-brand-400" };
  return { text: `${change}`, color: "text-error-400" };
}

function isDisputeWindowOpen(
  completedAt: string | null,
  disputeWindowHours: number | null | undefined,
): boolean {
  if (disputeWindowHours == null) {
    return true;
  }
  if (!completedAt) {
    return false;
  }

  const completedAtMs = new Date(completedAt).getTime();
  if (Number.isNaN(completedAtMs)) {
    return false;
  }

  const windowEndsMs = completedAtMs + disputeWindowHours * 60 * 60 * 1000;
  return Date.now() <= windowEndsMs;
}

/** Adapt a match player record to the FlowPlayer shape expected by PlayerCard. */
function toFlowPlayer(player: MatchData["players"][number]): FlowPlayer {
  return {
    userId: player.userId,
    username: player.username ?? "Unknown",
    avatarUrl: player.avatarUrl,
    displayElo: player.eloAtMatch ?? 0,
    team: player.team,
    partyId: player.partyId ?? null,
    isConnected: true,
    isHost: player.isHost ?? false,
    lastSeenAt: Date.now(),
  };
}

function getGameIdentity(player: MatchData["players"][number]): string | null {
  const gameIdentity = (player as { gameIdentity?: string | null }).gameIdentity;
  return gameIdentity ?? null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MatchCompletedView({
  match,
  clubSlug,
  disputeWindowHours = null,
}: MatchCompletedViewProps) {
  const { data: session } = useSession();
  const currentUserId = session?.user?.id ?? "";

  const teamCount = match.teamCount ?? 2;
  const teamNames = getTeamNames(match.id, teamCount);
  const duration = formatDuration(match.startedAt, match.completedAt);
  const rankConfig = (match.rankConfig ?? null) as QueueRankConfig | null;

  const myTeam = match.players.find((p) => p.userId === currentUserId)?.team;
  const isParticipant = myTeam != null;
  const isDraw = match.winningTeam == null;
  const didWin = isParticipant && !isDraw && match.winningTeam === myTeam;
  const didLose = isParticipant && !isDraw && match.winningTeam !== myTeam;
  const filingWindowOpen = isDisputeWindowOpen(match.completedAt, disputeWindowHours);

  const disputeQuery = trpc.dispute.getByMatch.useQuery(
    { matchId: match.id },
    {
      enabled: isParticipant,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  // Group players by team
  const teamGroups = new Map<number, MatchData["players"]>();
  for (const player of match.players) {
    if (!teamGroups.has(player.team)) teamGroups.set(player.team, []);
    teamGroups.get(player.team)?.push(player);
  }

  const sortedTeams = [...teamGroups.entries()].sort(([a], [b]) => a - b);

  // For 2-team: winning team first (skip reorder on draw)
  const orderedTeams =
    teamCount === 2 && match.winningTeam != null
      ? sortedTeams.sort(([a]) => (a === match.winningTeam ? -1 : 1))
      : sortedTeams;

  return (
    <div className="@container relative space-y-0">
      {/* ===== Hero Banner with map backdrop ===== */}
      <div className="relative rounded-xl overflow-hidden border border-surface-700/40 mb-6">
        <div className="absolute inset-0 noise-overlay pointer-events-none z-[1]" />

        {match.mapImageUrl ? (
          <div className="relative h-56 @sm:h-64">
            <img
              src={match.mapImageUrl}
              alt={match.mapName ?? "Map"}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Heavy gradient overlay -- the image is just texture */}
            <div className="absolute inset-0 bg-gradient-to-t from-surface-900 via-surface-900/70 to-surface-900/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-surface-900/20 via-transparent to-surface-900/20" />

            {/* Victory/Defeat color wash */}
            {didWin && (
              <div className="absolute inset-0 bg-gradient-to-t from-brand-500/[0.08] via-brand-500/[0.03] to-transparent" />
            )}
            {didLose && (
              <div className="absolute inset-0 bg-gradient-to-t from-error-500/[0.08] via-error-500/[0.03] to-transparent" />
            )}

            <ResultBanner
              didWin={didWin}
              didLose={didLose}
              isDraw={isDraw}
              isParticipant={isParticipant}
              winningTeam={match.winningTeam}
              teamNames={teamNames}
            />

            {/* Bottom bar: map + duration */}
            <div className="absolute bottom-0 left-0 right-0 z-10 flex items-end justify-between p-5">
              <div className="flex items-center gap-2.5">
                <MapIcon className="h-5 w-5 text-surface-400" />
                <span className="font-display text-base font-bold text-surface-300 uppercase tracking-wider">
                  {match.mapName ?? "Unknown Map"}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-surface-900/70 backdrop-blur-sm rounded-lg px-3.5 py-2 border border-surface-700/30">
                <Clock className="h-4 w-4 text-surface-500" />
                <span className="font-display font-bold text-base text-surface-300 tabular-nums tracking-wide">
                  {duration}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative h-40 bg-gradient-to-br from-surface-800/80 via-surface-850 to-surface-900">
            <ResultBanner
              didWin={didWin}
              didLose={didLose}
              isDraw={isDraw}
              isParticipant={isParticipant}
              winningTeam={match.winningTeam}
              teamNames={teamNames}
            />
            <div className="absolute bottom-0 left-0 right-0 z-10 flex items-end justify-between p-5">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-surface-500" />
                <span className="font-display font-bold text-sm text-surface-300 tabular-nums">
                  {duration}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== Info Pills ===== */}
      <div className="flex flex-wrap items-center gap-2.5 mb-6">
        {match.gameName && (
          <div className="flex items-center gap-2 bg-surface-800/60 border border-surface-700/30 rounded-lg px-3 py-2">
            {match.gameIconUrl ? (
              <img src={match.gameIconUrl} alt="" className="h-5 w-5 rounded-sm object-cover" />
            ) : null}
            <span className="text-sm font-display font-semibold text-surface-300">
              {match.gameName}
            </span>
          </div>
        )}
        {match.queueName && (
          <div className="flex items-center gap-2 bg-surface-800/60 border border-surface-700/30 rounded-lg px-3 py-2">
            <span className="text-sm font-display font-semibold text-surface-300">
              {match.queueName}
            </span>
          </div>
        )}
      </div>

      {isParticipant ? (
        <div className="mb-6 space-y-3">
          {disputeQuery.data ? (
            <DisputeStatusBanner currentUserId={currentUserId} dispute={disputeQuery.data} />
          ) : null}
          <DisputeButton
            existingDispute={
              disputeQuery.data
                ? {
                    id: disputeQuery.data.id,
                    status: disputeQuery.data.status,
                    withdrawnAt: disputeQuery.data.withdrawnAt,
                  }
                : null
            }
            filingWindowOpen={filingWindowOpen}
            loadError={disputeQuery.error?.message ?? null}
            isLoading={disputeQuery.isLoading}
            matchId={match.id}
          />
        </div>
      ) : null}

      {/* ===== Team Rosters with ELO changes ===== */}
      {teamCount === 2 && orderedTeams.length === 2 ? (
        <TwoTeamRosters
          teams={orderedTeams}
          teamNames={teamNames}
          winningTeam={match.winningTeam}
          isDraw={isDraw}
          currentUserId={currentUserId}
          rankConfig={rankConfig}
          myTeam={myTeam}
        />
      ) : (
        <MultiTeamRosters
          teams={orderedTeams}
          teamNames={teamNames}
          winningTeam={match.winningTeam}
          isDraw={isDraw}
          currentUserId={currentUserId}
          rankConfig={rankConfig}
          myTeam={myTeam}
        />
      )}

      {/* ===== Back button ===== */}
      <div className="flex justify-center pt-6 pb-4">
        <Link
          to={clubSlug ? "/app/clubs/$slug" : "/app"}
          params={clubSlug ? { slug: clubSlug } : undefined}
        >
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Club
          </Button>
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result Banner (centered in hero)
// ---------------------------------------------------------------------------

function ResultBanner({
  didWin,
  didLose,
  isDraw,
  isParticipant,
  winningTeam,
  teamNames,
}: {
  didWin: boolean;
  didLose: boolean;
  isDraw: boolean;
  isParticipant: boolean;
  winningTeam: number | null;
  teamNames: string[];
}) {
  const winnerName = winningTeam != null ? (teamNames[winningTeam] ?? `Team ${winningTeam}`) : null;

  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
      {/* Result icon */}
      <div
        className={cn(
          "w-14 h-14 rounded-full flex items-center justify-center mb-3",
          didWin && "bg-brand-500/20",
          didLose && "bg-error-500/15",
          isDraw && "bg-amber-500/15",
          !isParticipant && !isDraw && "bg-surface-700/40",
        )}
      >
        {didWin ? (
          <Trophy className="h-7 w-7 text-brand-400" />
        ) : didLose ? (
          <Skull className="h-7 w-7 text-error-400" />
        ) : isDraw ? (
          <Minus className="h-7 w-7 text-amber-400" />
        ) : (
          <Trophy className="h-7 w-7 text-surface-500" />
        )}
      </div>

      {/* Main result text */}
      <h1
        className={cn(
          "font-display text-4xl @sm:text-5xl font-black uppercase tracking-[0.15em] leading-none",
          didWin && "text-brand-400",
          didLose && "text-error-400",
          isDraw && "text-amber-400",
          !isParticipant && !isDraw && "text-white",
        )}
      >
        {didWin ? "VICTORY" : didLose ? "DEFEAT" : isDraw ? "DRAW" : "MATCH OVER"}
      </h1>

      {/* Subtitle -- who won */}
      {winnerName && (
        <p className="mt-2.5 text-base font-display font-semibold text-surface-400 uppercase tracking-wider">
          {didWin ? "Your team dominated" : `${winnerName} wins`}
        </p>
      )}
      {isDraw && (
        <p className="mt-2.5 text-base font-display font-semibold text-surface-400 uppercase tracking-wider">
          No winner declared
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Two-Team VS Roster (winning team first)
// ---------------------------------------------------------------------------

function TwoTeamRosters({
  teams,
  teamNames,
  winningTeam,
  isDraw,
  currentUserId,
  rankConfig,
  myTeam,
}: {
  teams: [number, MatchData["players"]][];
  teamNames: string[];
  winningTeam: number | null;
  isDraw: boolean;
  currentUserId: string;
  rankConfig: QueueRankConfig | null;
  myTeam?: number;
}) {
  return (
    <div className="grid grid-cols-1 @md:grid-cols-2 gap-3">
      {teams.map(([teamNum, players]) => {
        const isWinner = teamNum === winningTeam;
        const teamName = teamNames[teamNum] ?? `Team ${teamNum}`;

        return (
          <TeamRosterCard
            key={teamNum}
            teamNum={teamNum}
            teamName={teamName}
            players={players}
            isWinner={isWinner}
            isDraw={isDraw}
            myTeam={myTeam}
            currentUserId={currentUserId}
            rankConfig={rankConfig}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-team roster (3+ teams)
// ---------------------------------------------------------------------------

function MultiTeamRosters({
  teams,
  teamNames,
  winningTeam,
  isDraw,
  currentUserId,
  rankConfig,
  myTeam,
}: {
  teams: [number, MatchData["players"]][];
  teamNames: string[];
  winningTeam: number | null;
  isDraw: boolean;
  currentUserId: string;
  rankConfig: QueueRankConfig | null;
  myTeam?: number;
}) {
  return (
    <div className="grid grid-cols-1 @sm:grid-cols-2 gap-3">
      {teams.map(([teamNum, players]) => {
        const isWinner = teamNum === winningTeam;
        const teamName = teamNames[teamNum] ?? `Team ${teamNum}`;

        return (
          <TeamRosterCard
            key={teamNum}
            teamNum={teamNum}
            teamName={teamName}
            players={players}
            isWinner={isWinner}
            isDraw={isDraw}
            myTeam={myTeam}
            currentUserId={currentUserId}
            rankConfig={rankConfig}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team Roster Card
// ---------------------------------------------------------------------------

function TeamRosterCard({
  teamNum,
  teamName,
  players,
  isWinner,
  isDraw,
  myTeam,
  currentUserId,
  rankConfig,
}: {
  teamNum: number;
  teamName: string;
  players: MatchData["players"];
  isWinner: boolean;
  isDraw: boolean;
  myTeam?: number;
  currentUserId: string;
  rankConfig: QueueRankConfig | null;
}) {
  const isMyTeam = myTeam != null && teamNum === myTeam;
  const avgElo = Math.round(
    players.reduce((sum, p) => sum + (p.eloAtMatch ?? 0), 0) / players.length,
  );
  const teamColor = getTeamColor(teamNum, myTeam);

  // Outcome-based colors: winners = brand teal, draw = amber, losers = red
  const accentColor = isWinner ? "bg-brand-500" : isDraw ? "bg-amber-500" : "bg-error-500";
  const textColor = isWinner ? "text-brand-400" : isDraw ? "text-amber-400" : "text-error-400";

  return (
    <div
      className={cn(
        "relative rounded-xl border overflow-hidden",
        isWinner
          ? "border-brand-500/25 bg-brand-500/[0.04]"
          : isDraw
            ? "border-amber-500/20 bg-amber-500/[0.03]"
            : "border-error-500/20 bg-error-500/[0.02]",
      )}
    >
      {/* Top accent line */}
      <div className={cn("absolute top-0 left-0 right-0 h-0.5", accentColor)} />

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2.5">
        <div className="flex items-center gap-2.5">
          {isWinner && <Crown className={cn("h-5 w-5", textColor)} />}
          <div className={cn("w-1 h-5 rounded-full", teamColor.accent)} />
          <h3
            className={cn("text-base font-display font-bold uppercase tracking-wider", textColor)}
          >
            {teamName}
          </h3>
          {isMyTeam && (
            <span
              className={cn(
                "text-xs font-display font-semibold uppercase",
                isWinner ? "text-brand-500/60" : isDraw ? "text-amber-500/50" : "text-error-500/50",
              )}
            >
              (you)
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-display font-semibold text-surface-500 tabular-nums">
            AVG {avgElo}
          </span>
          {isWinner ? (
            <span className="text-xs font-display font-bold text-brand-400 uppercase tracking-wider">
              Winner
            </span>
          ) : isDraw ? (
            <span className="text-xs font-display font-bold text-amber-400/60 uppercase tracking-wider">
              Draw
            </span>
          ) : (
            <span className="text-xs font-display font-bold text-error-400/60 uppercase tracking-wider">
              Defeat
            </span>
          )}
        </div>
      </div>

      {/* Player rows */}
      <div className="px-2.5 pb-2.5 space-y-0.5">
        {players.map((player) => {
          const isMe = player.userId === currentUserId;
          const elo = formatEloChange(player.eloChange);

          return (
            <PlayerCard
              key={player.userId}
              player={toFlowPlayer(player)}
              variant="compact"
              isMe={isMe}
              rankConfig={rankConfig}
              gameIdentity={getGameIdentity(player)}
              rightSlot={
                <div className="flex items-center gap-2">
                  {/* ELO at match */}
                  <span className="text-sm font-display font-semibold text-surface-500 tabular-nums shrink-0 w-12 text-right">
                    {player.eloAtMatch ?? "\u2014"}
                  </span>
                  {/* ELO change */}
                  <span
                    className={cn(
                      "text-sm font-display font-bold tabular-nums shrink-0 w-12 text-right",
                      elo.color,
                    )}
                  >
                    {player.eloChange != null ? (
                      elo.text
                    ) : (
                      <Minus className="h-3.5 w-3.5 text-surface-600 ml-auto" />
                    )}
                  </span>
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}
